import { deleteApiWithData, getApi } from 'apis';
import { SideDrawerWrapper } from 'components/Global';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import { TablePagination, TableSorter } from 'interfaces/global.interface';
import { useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import AddDeductionBody from './AddDeductionBody';
import DeductionHeader from './Table/Header';
import styles from './index.module.scss';
import DeductionTable from './Table/DeductionTable';
import useDebounce from 'components/Global/Hooks/UseDebounce';

const DeductionComponent = () => {
	const [searchValue, setSearchValue] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalRecords, setTotalRecords] = useState(0);
	const [sortField, setSortField] = useState('');
	const [sortOrder, setSortOrder] = useState('');
	const [drawerAnimation, setDrawerAnimation] = useState<boolean>(false);
	const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
	const [isSideDrawerOpen, setIsSideDrawerOpen] = useState<boolean>(false);
	const [isThirdPartyLoan, setIsThirdPartyLoan] = useState<boolean>(false);
	const [isLoanEditable, setIsLoanEditable] = useState<boolean>(false);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [edit, setEdit] = useState<any>();
	const [isLoading, setIsLoading] = useState(false);
	const [drawerInfo, setDrawerInfo] = useState({
		drawerTitle: 'Deduction',
	});
	const [deductionData, setDeductionData] = useState([]);
	const debouncedSearchValue = useDebounce(searchValue, 500);
	const buttons = [
		{
			text: 'Add Deduction',
			isLoading: false,
			className: 'btn-blue',
			// icon: <AddSvg />,
			isSubmit: true,
			onclick: () => {
				openDrawerHandler();
			},
			disabled: isLoading,
		},
	];

	const handleSearchApi = async (value: string) => {
		//search api
	};

	const handleSearch = (value: string) => {
		setSearchValue(value);
		setCurrentPage(1);
	};

	const tableChangeHandler = (
		pagination: TablePagination,
		filters: any,
		sorter: TableSorter
	) => {
		setTotalRecords(pagination.total);
		setCurrentPage(pagination.current);
		setPageSize(pagination.pageSize);
		let order = '';
		if (sorter?.order === 'ascend') {
			order = 'asc';
		} else if (sorter?.order) {
			order = 'desc';
		}
		setSortOrder(order);
		setSortField(sorter.field);
	};

	const refreshDeduction = () => {
		setCurrentPage(1);
		fetchDeduction();
	};

	// Open side drawer with animation
	const openDrawerHandler = () => {
		setDrawerInfo({ drawerTitle: 'Add Deduction' });
		setDrawerAnimation(true);
		setIsSideDrawerOpen(true);
	};
	// Remove the side drawer
	const removeDrawerFromDom = () => {
		setIsSideDrawerOpen(false);
	};

	// Close the side drawer with animation
	const closeDrawerByAnimation = () => {
		setEdit(null); //!isAddUserLoading &&
		setDrawerAnimation(false); //!isAddUserLoading &&
	};

	const fetchDeduction = async () => {
		try {
			setIsLoading(true);

			const query = {
				page: currentPage,
				search: debouncedSearchValue.trim(),
				sortBy: sortField,
				sortOrder: sortOrder,
				pageSize: pageSize,
			};

			const deductions = await getApi('/deduction', query);

			setDeductionData(deductions.data.data);
			setTotalRecords(deductions.data.total);
			setIsThirdPartyLoan(deductions.data.isThirdPartyLoan);
			setIsLoanEditable(deductions.data.isLoanEditable);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching deduction';
			toastText(errorMessage, 'error');
		} finally {
			setIsLoading(false);
		}
	};
	// Change data and title between components
	const setDrawerInfoHandler = (drawerTitle: any) => {
		setDrawerInfo({ drawerTitle });
	};

	// Confirm operation
	const handleOk = () => {
		setIsModalOpen(false);
		setEdit(null);
	};

	// Cancel operation
	const handleCancel = () => {
		setIsModalOpen(false); // !isAddUserLoading &&
		setEdit(null);
	};

	const showModal = () => {
		setIsModalOpen(true);
	};

	// Delete user
	const deleteHandler = async () => {
		if (edit) {
			try {
				setIsDeleteLoading(true);
				const response = await deleteApiWithData('/deduction', {
					deductionCodeId: edit.id,
				});
				if (totalRecords > 1 && totalRecords % pageSize === 1) {
					setCurrentPage(currentPage - 1);
				}
				handleCancel();
				fetchDeduction();
				let message =
					response?.data?.message || 'Deduction deleted successfully';
				toastText(message, 'success');
			} catch (error: any) {
				const errorMessage =
					error?.response?.data?.message ||
					'Something went wrong in delete deduction';
				toastText(errorMessage, 'error');
			} finally {
				setIsDeleteLoading(false);
			}
		}
	};

	useEffect(() => {
		fetchDeduction();
	}, [pageSize, debouncedSearchValue, currentPage, sortOrder, sortField]);
	return (
		<>
			<div className={styles['employee-container']}>
				<DeductionHeader
					searchValue={searchValue}
					handleSearch={handleSearch}
					handleSearchApi={handleSearchApi}
					button={buttons}
					isLoading={isLoading}
					fetchAllDeductions={refreshDeduction}
				/>
				<DeductionTable
					deductionData={deductionData}
					totalRecords={totalRecords}
					pageSize={pageSize}
					currentPage={currentPage}
					tableChangeHandler={tableChangeHandler}
					fetchRecords={fetchDeduction}
					setEditData={setEdit}
					openDrawerHandler={openDrawerHandler}
					setDrawerInfoHandler={setDrawerInfoHandler}
					showModal={showModal}
					isLoading={isLoading}
				/>
			</div>
			{isSideDrawerOpen && (
				<SideDrawerWrapper
					isOpen={drawerAnimation}
					removeDrawerFromDom={removeDrawerFromDom}
					closeDrawerByAnimation={closeDrawerByAnimation}
					headerTitle={drawerInfo.drawerTitle}
					position="right"
					width="half"
				>
					<AddDeductionBody
						closeDrawerByAnimation={closeDrawerByAnimation}
						fetchDeductions={fetchDeduction}
						editSelected={edit}
						setEditSelectedUser={setEdit}
						isThirdPartyLoan={isThirdPartyLoan}
						isLoanEditable={isLoanEditable}
					/>
				</SideDrawerWrapper>
			)}
			<ConfirmDelete
				handleCancel={handleCancel}
				handleOk={handleOk}
				isModalOpen={isModalOpen}
				deleteHandler={deleteHandler}
				isLoading={isDeleteLoading}
			/>
		</>
	);
};

export default DeductionComponent;
